import type React from 'react'
import {useEffect, useRef, useState} from 'react'
import * as FullStory from '@fullstory/browser'
import {getUserProfileFromOkta} from '../../utils/userHelper'
import {FULLSTORY_ORG} from '../../config/envConfig'
import {initFullStory} from './initFullStory'
import {useOktaAuth} from '@okta/okta-react'

// Add a declaration for the FullStory namespace based on v1.7.1
declare global {
  interface Window {
    FS?: typeof FullStory
  }
}

interface FullstoryWrapperProps {
  children: React.ReactNode
}

/**
 * FullstoryWrapper component
 *
 * This component initializes FullStory and identifies the user when available.
 * It uses environment configuration from envConfig.ts.
 */
export const FullstoryWrapper = ({children}: FullstoryWrapperProps) => {
  const hasInitialized = useRef(false)
  const hasIdentified = useRef(false)
  const {authState} = useOktaAuth()
  const [retryCount, setRetryCount] = useState(0)

  // Initialize FullStory once
  useEffect(() => {
    if (!hasInitialized.current) {
      initFullStory()
      hasInitialized.current = true
    }
  }, [])

  // Identify user when profile is available - retry mechanism
  useEffect(() => {
    // Only proceed if FullStory is configured and user is authenticated
    if (!hasIdentified.current && FULLSTORY_ORG && FULLSTORY_ORG.trim() !== '' && authState?.isAuthenticated) {
      try {
        const userProfile = getUserProfileFromOkta()

        // If profile is available with all required fields, identify the user
        if (userProfile?.gpId && userProfile?.email && userProfile?.name) {
          FullStory.identify(userProfile.gpId, {
            displayName_str: userProfile.name,
            email_str: userProfile.email,
          })
          hasIdentified.current = true
          console.log('[FullStory] User identified:', userProfile.email)
        } else if (retryCount < 5) {
          // Retry after a delay if profile is not fully loaded yet
          console.log('[FullStory] User profile not ready, retrying...', retryCount + 1)
          const timer = setTimeout(() => {
            setRetryCount(prev => prev + 1)
          }, 1000)
          return () => clearTimeout(timer)
        } else {
          console.warn('[FullStory] Failed to identify user after 5 retries')
        }
      } catch (error) {
        console.error('[FullStory] Error identifying user:', error)
      }
    }
  }, [authState?.isAuthenticated, retryCount])

  return <>{children}</>
}

export default FullstoryWrapper
